//@flow
import * as React from 'react';
import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogFooter,
    Heading,
    Button,
    Text,
    Flex,
    Icon,
    Tooltip
} from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';
import { type ThemeType } from 'react-css-themr';
import {
    RadioGroup,
    RadioButton
} from 'common/components/core/form/RadioButton';

export const INCREMENTAL_PLAN_CONFIG_TYPE = Object.freeze({
    RETAIN_NO_BEATS: 'RETAIN_NO_BEATS',
    RETAIN_ALL_BEATS: 'RETAIN_ALL_BEATS',
    RETAIN_SOME_BEATS: 'RETAIN_SOME_BEATS'
});

type Props = {
    theme: ThemeType,
    onDismiss: () => void,
    selectPlanType: (
        type: $Values<typeof INCREMENTAL_PLAN_CONFIG_TYPE>
    ) => void,
    isOpen: boolean
};

const ComingSoonIconTooltip = props => {
    const { t } = useTranslation('entityManagement');

    return (
        <Flex alignItems="center">
            {props.children}
            <Tooltip tooltip={t('pjpCreate.incrementalPlan.comingSoon')}>
                <Icon icon="information" size={Icon.Size.medium} />
            </Tooltip>
        </Flex>
    );
};

const IncrementalConfigSelector = (props: Props): React.Node => {
    const { t } = useTranslation('entityManagement');
    const [selectedIncPlanType, setSelectedIncPlanType] = React.useState(
        INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_ALL_BEATS
    );
    const _onPlanTypeChange = React.useCallback(type => {
        setSelectedIncPlanType(type);
    }, []);
    const { onDismiss, selectPlanType } = props;
    const _onDismissPlan = React.useCallback(() => {
        onDismiss();
    }, [onDismiss]);
    const _selectFinalPlanType = React.useCallback(() => {
        selectPlanType(selectedIncPlanType);
    }, [selectPlanType, selectedIncPlanType]);
    return (
        <Dialog isOpen={props.isOpen} sticky onDismiss={_onDismissPlan}>
            <DialogHeader>
                <Heading level={4}>
                    {t('pjpCreate.incrementalPlan.createIncrementalPlan')}
                </Heading>
            </DialogHeader>
            <DialogContent>
                <Text fontSize={2}>
                    {t(
                        'pjpCreate.incrementalPlan.createIncrementalPlanBodyTitle'
                    )}
                </Text>
                <RadioGroup
                    onChange={_onPlanTypeChange}
                    value={selectedIncPlanType}
                    theme={props.theme}
                >
                    <RadioButton
                        theme={props.theme}
                        label={t('pjpCreate.incrementalPlan.dontRetainBeats')}
                        value={INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_NO_BEATS}
                    />
                    <RadioButton
                        theme={props.theme}
                        label={t('pjpCreate.incrementalPlan.retainBeats')}
                        value={INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_ALL_BEATS}
                    />
                    <RadioButton
                        theme={props.theme}
                        disabled
                        label={
                            <ComingSoonIconTooltip>
                                {t('pjpCreate.incrementalPlan.retainSomeBeats')}
                            </ComingSoonIconTooltip>
                        }
                        value={INCREMENTAL_PLAN_CONFIG_TYPE.RETAIN_SOME_BEATS}
                    />
                </RadioGroup>
            </DialogContent>
            <DialogFooter>
                <Button onClick={_onDismissPlan}>
                    {t('translations:cancel')}
                </Button>
                <Button
                    appearance="solid"
                    palette="primary"
                    onClick={_selectFinalPlanType}
                >
                    {t('translations:next')}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default IncrementalConfigSelector;
